/**
 * Pipeline Revenue Leak Assessment - Scoring Utilities
 * 
 * Handles all score calculations, revenue leak estimates,
 * and insight generation based on quiz answers.
 */

import { sections, sectionInsights, overallInsights, resultsConfig } from '../data/questions';

/**
 * Calculate score for a single section
 * @param {string} sectionId - The section ID
 * @param {Object} answers - Object mapping questionId to selected answer
 * @returns {Object} { score, maxScore, percentage }
 */
export const calculateSectionScore = (sectionId, answers) => {
  const section = sections.find(s => s.id === sectionId);
  
  if (!section || !section.scorable) {
    return { score: 0, maxScore: 0, percentage: 0 };
  }

  let score = 0;
  
  section.questions.forEach(question => {
    const selectedAnswer = answers[question.id];
    if (selectedAnswer) {
      const answer = question.answers.find(a => a.id === selectedAnswer);
      if (answer && typeof answer.points === 'number') {
        score += answer.points;
      }
    }
  });

  const percentage = Math.round((score / section.maxPoints) * 100);

  return {
    score,
    maxScore: section.maxPoints,
    percentage
  };
};

/**
 * Calculate all section scores
 * @param {Object} answers - Object mapping questionId to selected answer
 * @returns {Object} Scores keyed by section ID
 */
export const calculateAllSectionScores = (answers) => {
  const scores = {};

  sections.forEach(section => {
    if (section.scorable) {
      scores[section.id] = calculateSectionScore(section.id, answers);
    }
  });

  return scores;
};

/**
 * Calculate overall score from all sections
 * @param {Object} sectionScores - Output from calculateAllSectionScores
 * @returns {Object} { score, maxScore, percentage }
 */
export const calculateOverallScore = (sectionScores) => {
  let totalScore = 0;
  let totalMaxScore = 0;

  Object.values(sectionScores).forEach(({ score, maxScore }) => {
    totalScore += score;
    totalMaxScore += maxScore;
  });

  const percentage = totalMaxScore > 0 
    ? Math.round((totalScore / totalMaxScore) * 100) 
    : 0;

  return {
    score: totalScore,
    maxScore: totalMaxScore,
    percentage
  };
};

/**
 * Get revenue data from Q1 answer
 * @param {Object} answers - Quiz answers
 * @returns {Object} { bracket, midpoint, multiplier }
 */
export const getRevenueData = (answers) => {
  const contextSection = sections.find(s => s.id === 'context');
  const revenueQuestion = contextSection?.questions.find(q => q.id === 'q1');
  const selectedAnswerId = answers['q1'];
  
  if (!revenueQuestion || !selectedAnswerId) {
    // Default fallback
    return {
      bracket: 'unknown',
      midpoint: 500000,
      multiplier: 0.18
    };
  }

  const selectedAnswer = revenueQuestion.answers.find(a => a.id === selectedAnswerId);
  
  return {
    bracket: selectedAnswer?.value || 'unknown',
    midpoint: selectedAnswer?.revenueMidpoint || 500000,
    multiplier: selectedAnswer?.revenueMultiplier || 0.18
  };
};

/**
 * Calculate estimated revenue leak
 * @param {Object} answers - Quiz answers
 * @param {number} overallPercentage - Overall score percentage (0-100)
 * @returns {Object} { low, high, formatted }
 */
export const calculateRevenueLeak = (answers, overallPercentage) => {
  const { midpoint, multiplier } = getRevenueData(answers);
  
  // Leak severity is inverse of score (higher score = lower leak)
  const leakSeverity = 1 - (overallPercentage / 100);
  
  // Base leak calculation
  const baseLeak = midpoint * multiplier;
  const estimatedLeak = baseLeak * leakSeverity;
  
  // Add variance for range display (±30%)
  const lowEstimate = Math.round(estimatedLeak * 0.7);
  const highEstimate = Math.round(estimatedLeak * 1.3);
  
  // Format as currency
  const formatCurrency = (num) => {
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    }
    return `$${Math.round(num / 1000).toLocaleString()}K`;
  };

  return {
    low: lowEstimate,
    high: highEstimate,
    formatted: `${formatCurrency(lowEstimate)} - ${formatCurrency(highEstimate)}`,
    exact: Math.round(estimatedLeak)
  };
};

/**
 * Determine score level based on percentage
 * @param {number} percentage - Score percentage (0-100)
 * @returns {string} 'poor' | 'moderate' | 'good'
 */
export const getScoreLevel = (percentage) => {
  const { scoreThresholds } = resultsConfig;
  
  if (percentage <= scoreThresholds.poor) {
    return 'poor';
  } else if (percentage <= scoreThresholds.moderate) {
    return 'moderate';
  }
  return 'good';
};

/**
 * Get color for score gauge
 * @param {number} percentage - Score percentage (0-100)
 * @returns {Object} { color, bgColor, icon }
 */
export const getScoreColor = (percentage) => {
  const level = getScoreLevel(percentage);
  
  const colors = {
    poor: {
      color: '#ef4444',      // Red
      text: 'text-leak-red',  // Tailwind class
      bgColor: '#fee2e2',    // Light red
      bg: 'bg-leak-red',     // Tailwind class
      icon: '🔴',
      label: 'Needs Attention'
    },
    moderate: {
      color: '#eab308',      // Yellow
      text: 'text-caution-yellow',  // Tailwind class
      bgColor: '#fef9c3',    // Light yellow
      bg: 'bg-caution-yellow',     // Tailwind class
      icon: '⚠️',
      label: 'Room for Improvement'
    },
    good: {
      color: '#22c55e',      // Green
      text: 'text-healthy-green',  // Tailwind class
      bgColor: '#dcfce7',    // Light green
      bg: 'bg-healthy-green',     // Tailwind class
      icon: '✅',
      label: 'Healthy'
    }
  };

  return colors[level];
};

/**
 * Get insight text for a section based on its score
 * @param {string} sectionId - Section ID
 * @param {number} percentage - Section score percentage
 * @returns {string} Insight text
 */
export const getSectionInsight = (sectionId, percentage) => {
  const level = getScoreLevel(percentage);
  return sectionInsights[sectionId]?.[level] || '';
};

/**
 * Get overall insight based on total score
 * @param {number} percentage - Overall score percentage
 * @returns {Object} { headline, message }
 */
export const getOverallInsight = (percentage) => {
  const level = getScoreLevel(percentage);
  return overallInsights[level] || overallInsights.moderate;
};

/**
 * Identify the weakest section (biggest leak)
 * @param {Object} sectionScores - Scores keyed by section ID
 * @returns {Object} { sectionId, sectionName, percentage }
 */
export const identifyBiggestLeak = (sectionScores) => {
  let weakest = null;
  let lowestPercentage = 101;

  Object.entries(sectionScores).forEach(([sectionId, { percentage }]) => {
    if (percentage < lowestPercentage) {
      lowestPercentage = percentage;
      const section = sections.find(s => s.id === sectionId);
      weakest = {
        sectionId,
        sectionName: section?.name || sectionId,
        percentage
      };
    }
  });

  return weakest;
};

/**
 * Get specific insights from low-scoring answers
 * @param {Object} answers - Quiz answers
 * @returns {Array} Array of insight strings
 */
export const getSpecificInsights = (answers) => {
  const insights = [];

  sections.forEach(section => {
    if (!section.scorable) return;

    section.questions.forEach(question => {
      const selectedAnswerId = answers[question.id];
      if (!selectedAnswerId) return;

      const selectedAnswer = question.answers.find(a => a.id === selectedAnswerId);
      
      // Only include insights for low-scoring answers
      if (selectedAnswer && selectedAnswer.points <= 5 && selectedAnswer.insight) {
        insights.push({
          questionId: question.id,
          sectionId: section.id,
          insight: selectedAnswer.insight,
          severity: selectedAnswer.points === 0 ? 'high' : 'medium'
        });
      }
    });
  });

  // Sort by severity (high first) and limit to top 3
  return insights
    .sort((a, b) => (a.severity === 'high' ? -1 : 1))
    .slice(0, 3);
};

/**
 * Generate complete results object
 * @param {Object} answers - Quiz answers
 * @returns {Object} Complete results data for rendering
 */
export const generateResults = (answers) => {
  const sectionScores = calculateAllSectionScores(answers);
  const overall = calculateOverallScore(sectionScores);
  const revenueLeak = calculateRevenueLeak(answers, overall.percentage);
  const biggestLeak = identifyBiggestLeak(sectionScores);
  const specificInsights = getSpecificInsights(answers);
  const overallInsight = getOverallInsight(overall.percentage);

  // Get industry for context
  const industryAnswer = sections
    .find(s => s.id === 'context')
    ?.questions.find(q => q.id === 'q2')
    ?.answers.find(a => a.id === answers['q2']);

  return {
    overall: {
      ...overall,
      level: getScoreLevel(overall.percentage),
      color: getScoreColor(overall.percentage),
      insight: overallInsight
    },
    sections: Object.entries(sectionScores).map(([sectionId, scores]) => ({
      id: sectionId,
      name: sections.find(s => s.id === sectionId)?.name || sectionId,
      description: sections.find(s => s.id === sectionId)?.description || '',
      ...scores,
      level: getScoreLevel(scores.percentage),
      color: getScoreColor(scores.percentage),
      insight: getSectionInsight(sectionId, scores.percentage)
    })),
    revenueLeak,
    biggestLeak,
    specificInsights,
    context: {
      revenueBracket: getRevenueData(answers).bracket,
      industry: industryAnswer?.value || 'unknown',
      industryLabel: industryAnswer?.text || 'Unknown'
    }
  };
};

/**
 * Generate webhook payload for Zoho CRM
 * @param {Object} answers - Quiz answers
 * @param {Object} leadInfo - Lead capture form data
 * @param {Object} metadata - Additional metadata (timing, UTM, etc.)
 * @returns {Object} Webhook payload
 */
export const generateWebhookPayload = (answers, leadInfo, metadata = {}) => {
  const results = generateResults(answers);
  
  return {
    // Lead info
    first_name: leadInfo.firstName || '',
    last_name: leadInfo.lastName || '',
    full_name: leadInfo.fullName || '',
    email: leadInfo.email || '',
    company_name: leadInfo.companyName || '',
    company_website: leadInfo.companyWebsite || '',
    
    // Context
    revenue_bracket: results.context.revenueBracket,
    industry: results.context.industry,
    
    // Scores
    top_of_funnel_score: results.sections.find(s => s.id === 'top-of-funnel')?.score || 0,
    top_of_funnel_percentage: results.sections.find(s => s.id === 'top-of-funnel')?.percentage || 0,
    middle_of_funnel_score: results.sections.find(s => s.id === 'middle-of-funnel')?.score || 0,
    middle_of_funnel_percentage: results.sections.find(s => s.id === 'middle-of-funnel')?.percentage || 0,
    bottom_of_funnel_score: results.sections.find(s => s.id === 'bottom-of-funnel')?.score || 0,
    bottom_of_funnel_percentage: results.sections.find(s => s.id === 'bottom-of-funnel')?.percentage || 0,
    overall_score: results.overall.score,
    overall_percentage: results.overall.percentage,
    
    // Revenue leak
    estimated_leak_low: results.revenueLeak.low,
    estimated_leak_high: results.revenueLeak.high,
    estimated_leak_formatted: results.revenueLeak.formatted,
    
    // Analysis
    biggest_gap: results.biggestLeak?.sectionId || '',
    score_level: results.overall.level,
    
    // Metadata
    time_to_complete_seconds: metadata.timeToComplete || 0,
    completed_at: new Date().toISOString(),
    source: 'quiz.thryv.ing',
    utm_source: metadata.utmSource || '',
    utm_medium: metadata.utmMedium || '',
    utm_campaign: metadata.utmCampaign || ''
  };
};

export default {
  calculateSectionScore,
  calculateAllSectionScores,
  calculateOverallScore,
  getRevenueData,
  calculateRevenueLeak,
  getScoreLevel,
  getScoreColor,
  getSectionInsight,
  getOverallInsight,
  identifyBiggestLeak,
  getSpecificInsights,
  generateResults,
  generateWebhookPayload
};
